import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Autocomplete,
  Stack,
  Typography,
  Alert,
  Tooltip,
  IconButton,
  Divider,
  Box,
  CircularProgress,
  RadioGroup,
  FormControlLabel,
  Radio
} from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import { fetchUsersList } from '../../../User/Domain/Services/UserService';
import { fetchJobPositionsList } from '../../../JobPosition/Domain/Services/JobPositionService';
import { getPositionOrgChartList } from '../../../PositionOrgChart/Domain/Services/GetPositionOrgChartListService';
import * as AdminService from '../../Domain/Services/OrgChartAdminServices';

const OrgChartNodeEditor = ({ open, node, onClose, onActionComplete, allowChildCreation = true, allowEmployeeAssignment = true }) => {
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(false);
  const [feedback, setFeedback] = useState(null);
  
  // Form States
  const [title, setTitle] = useState('');
  const [chartType, setChartType] = useState('');
  const [positionId, setPositionId] = useState(null);
  const [originalPositionId, setOriginalPositionId] = useState(null);
  const [userId, setUserId] = useState(null);
  const [nodeType, setNodeType] = useState('area');
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  // Search Data
  const [users, setUsers] = useState([]);
  const [jobPositions, setJobPositions] = useState([]);
  const [positionOrgCharts, setPositionOrgCharts] = useState([]);

  const loadSearchData = useCallback(async () => {
    setLoadingData(true);
    try {
      const [userList, jobPositionList, positionOrgChartResponse] = await Promise.all([
        allowEmployeeAssignment ? fetchUsersList() : Promise.resolve([]),
        fetchJobPositionsList(),
        getPositionOrgChartList({ offset: 0, limit: 5000 })
      ]);
      setUsers(userList || []);
      setJobPositions(jobPositionList || []);
      setPositionOrgCharts(positionOrgChartResponse?.records || []);
    } catch (err) {
      console.error("Error loading search data:", err);
    } finally {
      setLoadingData(false);
    }
  }, []);

  useEffect(() => {
    if (open && node) {
      setTitle(node.text || '');
      setChartType(node.chartType || '');
      setPositionId(null);
      setOriginalPositionId(null);
      setUserId(node.userId || null);
      setNodeType(node.positionId ? 'posicion' : 'area');
      setFeedback(null);
      
      loadSearchData();
    }
  }, [open, node, loadSearchData]);

  useEffect(() => {
    if (!open || !node || loadingData) return;

    let mappedJobPositionId = null;

    // Estrategia primaria: buscar por nombre (es lo que se muestra en la tarjeta)
    if (node.position) {
      mappedJobPositionId = jobPositions.find((jp) => jp.name === node.position)?.id || null;
    }

    // Fallback 1: mapear PositionOrgChart.id → JobPosition.id
    if (!mappedJobPositionId && node.positionId) {
      mappedJobPositionId =
        positionOrgCharts.find((p) => p.id === node.positionId)?.jobPosition?.id || null;
    }

    // Fallback 2: coincidencia directa de ID con JobPosition
    if (!mappedJobPositionId && node.positionId && jobPositions.some((jp) => jp.id === node.positionId)) {
      mappedJobPositionId = node.positionId;
    }

    setPositionId(mappedJobPositionId);
    setOriginalPositionId(mappedJobPositionId);
  }, [open, node, loadingData, positionOrgCharts, jobPositions]);

  const handleTypeChange = (e) => {
    const newType = e.target.value;
    setNodeType(newType);
    if (newType === 'area') {
      setPositionId(null);
      setUserId(null);
    } else {
      setTitle('');
    }
  };

  const handleCreateChild = async () => {
    setLoading(true);
    setFeedback(null);
    try {
      const res = await AdminService.createNode("Nuevo Sub-nodo", chartType, node.id);
      setFeedback({ type: 'success', message: `Ejecutado 'create': Creado nodo con ID ${res.id}` });
      onActionComplete(res.id);
    } catch (err) {
      setFeedback({ type: 'error', message: err.message });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSibling = async () => {
    setLoading(true);
    setFeedback(null);
    try {
      const res = await AdminService.createNode("Nuevo Nodo Hermano", chartType, node?.parent ?? null);
      setFeedback({ type: 'success', message: `Ejecutado 'create': Creado nodo hermano con ID ${res.id}` });
      onActionComplete(res.id);
    } catch (err) {
      setFeedback({ type: 'error', message: err.message });
    } finally {
      setLoading(false);
    }
  };

  const handleSaveAll = async () => {
    setLoading(true);
    setFeedback(null);
    const results = [];
    try {
      // 1. Título
      if (title !== (node.text || '')) {
        await AdminService.updateNode(node.id, title);
        results.push("Título actualizado.");
      }
      
      // 2. Puesto
      if (positionId !== originalPositionId) {
        await AdminService.assignPosition(node.id, positionId);
        const posName = jobPositions.find(p => p.id === positionId)?.name || positionId || 'Vaciado';
        results.push(`Puesto asignado: ${posName}`);
      }

      // 3. Empleado
      if (allowEmployeeAssignment && userId !== (node.userId || null)) {
        await AdminService.assignEmployee(node.id, userId);
        const userName = users.find(u => u.id === userId)?.name || userId || 'Vaciado';
        results.push(`Empleado asignado: ${userName}`);
      }

      if (results.length > 0) {
        setFeedback({ 
          type: 'success', 
          message: results.map((r, i) => <div key={i}>{r}</div>) 
        });
        onActionComplete();
      } else {
        setFeedback({ type: 'info', message: "No se detectarón cambios para guardar." });
      }
    } catch (err) {
      setFeedback({ type: 'error', message: err.message });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    setDeleteConfirmOpen(false);
    setLoading(true);
    setFeedback(null);
    try {
      await AdminService.deleteNode(node.id);
      setFeedback({ type: 'success', message: `Ejecutado 'delete': Nodo ${node.id} borrado.` });
      setTimeout(() => {
        onActionComplete();
        onClose();
      }, 1000);
    } catch (err) {
      setFeedback({ type: 'error', message: err.message });
    } finally {
      setLoading(false);
    }
  };

  const hasChanges = title !== (node?.text || '') || 
                     positionId !== originalPositionId || 
                     (allowEmployeeAssignment && userId !== (node?.userId || null));

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>Gestionar Nodo: {node?.text}</Typography>
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
        <Stack spacing={3} sx={{ mt: 1 }}>
          {feedback && (
            <Alert 
              severity={feedback.type} 
              onClose={() => setFeedback(null)}
              sx={{ fontWeight: 'bold' }}
            >
              <Typography variant="caption" sx={{ display: 'block', mb: 0.5, opacity: 0.8 }}>
                [Salida del Comando]
              </Typography>
              {feedback.message}
            </Alert>
          )}

          <Box>
            <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 'bold', mb: 1.5 }}>
              TIPO DE NODO
            </Typography>
            <RadioGroup 
              row 
              value={nodeType} 
              onChange={handleTypeChange}
              sx={{ bgcolor: 'grey.50', p: 1, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}
            >
              <FormControlLabel 
                value="area" 
                control={<Radio size="small" />} 
                label={<Typography variant="body2" sx={{ fontWeight: nodeType === 'area' ? 'bold' : 'normal' }}>ÁREA (Información)</Typography>} 
              />
              <FormControlLabel 
                value="posicion" 
                control={<Radio size="small" />} 
                label={<Typography variant="body2" sx={{ fontWeight: nodeType === 'posicion' ? 'bold' : 'normal' }}>POSICIÓN (Puesto/Empleado)</Typography>} 
              />
            </RadioGroup>
          </Box>

          <Divider />

          {/* Sección General */}
          <Box sx={{ display: nodeType === 'area' ? 'block' : 'none' }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
              <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 'bold' }}>DATOS GENERALES (ÁREA)</Typography>
              <Tooltip title="Equivalente al comando 'update <id> <titulo>'. Cambia el texto descriptivo del nodo.">
                <HelpOutlineIcon sx={{ fontSize: 16, color: 'text.secondary', cursor: 'help' }} />
              </Tooltip>
            </Stack>
            <TextField
              fullWidth
              label="Título del Nodo"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              size="small"
              placeholder="Ej: Departamento IT, Dirección General..."
            />
          </Box>

          {nodeType === 'posicion' && <Divider />}

          {/* Sección de Puesto */}
          <Box sx={{ display: nodeType === 'posicion' ? 'block' : 'none' }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
              <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 'bold' }}>ASIGNAR PUESTO</Typography>
              <Tooltip title="Equivalente al comando 'assign-position <id> <posId>'. Vincula este nodo a un cargo de la empresa.">
                <HelpOutlineIcon sx={{ fontSize: 16, color: 'text.secondary', cursor: 'help' }} />
              </Tooltip>
            </Stack>
            {loadingData ? (
              <TextField
                fullWidth
                label="Buscar Puesto..."
                size="small"
                disabled
                value="Cargando puestos..."
              />
            ) : (
              <Autocomplete
                options={jobPositions}
                getOptionLabel={(option) => option.name || ''}
                value={jobPositions.find(p => p.id === positionId) || null}
                onChange={(_, newValue) => setPositionId(newValue?.id || null)}
                renderInput={(params) => <TextField {...params} label="Buscar Puesto..." size="small" />}
                noOptionsText="No se encontraron puestos"
              />
            )}
          </Box>

          {/* Sección de Empleado */}
          {allowEmployeeAssignment && (
          <Box sx={{ display: nodeType === 'posicion' ? 'block' : 'none' }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
              <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 'bold' }}>ASIGNAR EMPLEADO</Typography>
              <Tooltip title="Equivalente al comando 'assign-employee <id> <userId>'. Vincula una persona específica a este nodo.">
                <HelpOutlineIcon sx={{ fontSize: 16, color: 'text.secondary', cursor: 'help' }} />
              </Tooltip>
            </Stack>
            {loadingData ? (
              <TextField
                fullWidth
                label="Buscar Persona..."
                size="small"
                disabled
                value="Cargando personas..."
              />
            ) : (
              <Autocomplete
                options={users}
                getOptionLabel={(option) => `${option.name} ${option.firstSurname} ${option.secondSurname}`.trim() || ''}
                value={users.find(u => u.id === userId) || null}
                onChange={(_, newValue) => setUserId(newValue?.id || null)}
                renderInput={(params) => <TextField {...params} label="Buscar Persona..." size="small" />}
                noOptionsText="No se encontraron personas"
              />
            )}
          </Box>
          )}

          <Divider />

          {/* Acciones de Estructura */}
          <Box>
            <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 'bold', mb: 1 }}>ESTRUCTURA</Typography>
            <Stack direction="row" spacing={2}>
              {allowChildCreation && (
                <Tooltip title="Equivalente al comando 'create <titulo> <tipo> <parentId>'. Crea un nuevo nodo que colgará de este.">
                  <Button 
                    variant="outlined" 
                    fullWidth 
                    onClick={handleCreateChild}
                    disabled={loading}
                  >
                    Añadir Hijo
                  </Button>
                </Tooltip>
              )}
              <Tooltip title="Crea un nuevo nodo al mismo nivel que el actual usando el mismo padre.">
                <Button 
                  variant="outlined" 
                  fullWidth 
                  onClick={handleCreateSibling}
                  disabled={loading}
                >
                  Añadir Hermano
                </Button>
              </Tooltip>
              <Tooltip title="Equivalente al comando 'delete <id>'. Elimina este nodo y todos sus descendientes permanentemente.">
                <Button 
                  variant="outlined" 
                  color="error" 
                  fullWidth 
                  startIcon={<DeleteIcon />}
                  onClick={() => setDeleteConfirmOpen(true)}
                  disabled={loading}
                >
                  Borrar Nodo
                </Button>
              </Tooltip>
            </Stack>
          </Box>
        </Stack>
      </DialogContent>
        <DialogActions sx={{ p: 2, bgcolor: '#f8f9fa' }}>
          <Button onClick={onClose} color="inherit">
            Cancelar
          </Button>
          <Box sx={{ flexGrow: 1 }} />
          {loading && <CircularProgress size={20} sx={{ mr: 2 }} />}
          <Button 
            onClick={handleSaveAll} 
            variant="contained" 
            color="primary" 
            disabled={loading || !hasChanges}
            startIcon={<SaveIcon />}
            sx={{ px: 4, fontWeight: 'bold' }}
          >
            GUARDAR CAMBIOS
          </Button>
        </DialogActions>

        <Dialog
          open={deleteConfirmOpen}
          onClose={() => setDeleteConfirmOpen(false)}
        >
          <DialogTitle sx={{ fontWeight: 'bold' }}>Confirmar eliminación</DialogTitle>
          <DialogContent>
            <Typography>¿Estás seguro de que quieres borrar este nodo? Se eliminará este nodo y todos sus hijos.</Typography>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setDeleteConfirmOpen(false)} color="inherit">
              Cancelar
            </Button>
            <Button onClick={handleDelete} color="error" variant="contained" autoFocus>
              Borrar
            </Button>
          </DialogActions>
        </Dialog>
      </Dialog>
    </>
  );
};

OrgChartNodeEditor.propTypes = {
  open: PropTypes.bool.isRequired,
  node: PropTypes.shape({
    id: PropTypes.number,
    text: PropTypes.string,
    chartType: PropTypes.string,
    parent: PropTypes.number,
    position: PropTypes.string,
    positionId: PropTypes.number,
    userId: PropTypes.number
  }),
  onClose: PropTypes.func.isRequired,
  onActionComplete: PropTypes.func.isRequired,
  allowChildCreation: PropTypes.bool,
  allowEmployeeAssignment: PropTypes.bool,
};

export default OrgChartNodeEditor;
